import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { UserDebug } from '../debug/UserDebug';
import { useTranslation } from 'react-i18next';
import { ProfileSidebar } from '../profile/influencer/ProfileSidebar';
import { HostSidebar } from '../profile/host/HostSidebar';
import GlobalNavBar from './GlobalNavBar';
import { ImpersonationBanner } from './ImpersonationBanner';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuthStore();
  const location = useLocation();
  const { t } = useTranslation(['navigation', 'common']);

  const isInfluencer = user?.userType === 'influencer';
  const isHostLike = user?.userType === 'brand' || user?.userType === 'host' || user?.userType === 'agency';
  const useCustomSidebar = isInfluencer || isHostLike || !user?.userType;

  const brandNavigation = [
    { name: t('navigation:dashboard'), href: '/dashboard', icon: HomeIcon },
    { name: t('navigation:findInfluencers'), href: '/search', icon: MagnifyingGlassIcon },
    { name: t('navigation:myProfile'), href: '/host/info', icon: UserIcon },
    { name: t('navigation:createCampaign'), href: '/campaigns/register', icon: PlusIcon },
    { name: t('navigation:myCampaigns'), href: '/campaigns', icon: BriefcaseIcon },
    { name: t('navigation:matches'), href: '/matches', icon: ChatBubbleLeftRightIcon },
    { name: t('navigation:messages'), href: '/messages', icon: ChatBubbleLeftRightIcon },
  ];

  const influencerNavigation = [
    { name: t('navigation:dashboard'), href: '/dashboard', icon: HomeIcon },
    { name: t('navigation:myProfile'), href: '/my-profile/info', icon: UserIcon },
    { name: t('navigation:discoverCampaigns'), href: '/discover', icon: MagnifyingGlassIcon },
    { name: t('navigation:myApplications'), href: '/matches', icon: BriefcaseIcon },
    { name: t('navigation:messages'), href: '/messages', icon: ChatBubbleLeftRightIcon },
  ];

  const navigation = user?.userType === 'host' ? brandNavigation : influencerNavigation;

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Global Nav Bar — full width, above everything */}
      <GlobalNavBar variant="dark" />

      {/* Desktop sidebar — starts below the navbar (top-16 = h-16 navbar height) */}
      <div className="hidden lg:fixed lg:top-16 lg:bottom-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-200">
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {useCustomSidebar ? (
              isHostLike
                ? <HostSidebar variant="embedded" />
                : <ProfileSidebar variant="embedded" />
            ) : (
              navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              ))
            )}
          </nav>
          <div className="px-4 py-4 border-t">
            <div className="flex items-center space-x-3">
              <img
                src={user?.profileImage || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user?.name || 'User') + '&background=6366f1&color=fff&size=40'}
                alt={user?.name}
                className="h-10 w-10 rounded-full"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">
                  {user?.userType
                    ? t(`common:userTypeLabels.${user.userType}`, { defaultValue: user.userType })
                    : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="flex-1">
          <ImpersonationBanner />
          {children}
        </main>
      </div>
      {process.env.NODE_ENV !== 'production' && <UserDebug />}
    </div>
  );
};
